import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { CHART_COLORS } from '../../utils/chartColors';

interface ObjectionCategoryData {
  category: string;
  count: number;
  percentage: number;
}

interface TopObjectionCategoriesChartProps {
  data: ObjectionCategoryData[];
  isLoading?: boolean;
  error?: string | null;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const TopObjectionCategoriesChart: React.FC<TopObjectionCategoriesChartProps> = ({
  data,
  isLoading = false,
  error = null,
}) => {
  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    const categories = data.map((d) => d.category);
    const counts = data.map((d) => d.count);
    const percentages = data.map((d) => d.percentage);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = data[idx];
          if (!item) return '';
          return `<b>${item.category}</b><br/>Count: ${item.count}<br/>Percentage: ${item.percentage.toFixed(1)}%`;
        },
      },
      grid: {
        left: '3%',
        right: '12%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 120,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((count, idx) => ({
            value: count,
            itemStyle: {
              color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: (params: any) => {
              const idx = params.dataIndex;
              return `${params.value} (${percentages[idx]?.toFixed(1)}%)`;
            },
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Top Objection Categories"
      subtitle="Most common objection types across conversations"
      isLoading={isLoading}
      error={error}
    >
      {(!data || data.length === 0) && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, data.length * 35) + 'px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default TopObjectionCategoriesChart;
